import React, { memo } from 'react'
import styled from 'styled-components'
import { Text, Devices, Spacings } from '@styles'

const LeftTabGroup = styled.div`
	display: flex;
	align-items: stretch;
	overflow-x: auto;
	flex: 0 1 auto;
	min-width: 0;
	width: fit-content;
	background: ${({ theme }) => theme.colors.surface.card};
	border-radius: ${({ theme }) => theme.radius.lg};
	box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.border.default}, ${({ theme }) => theme.shadows.xs};
	height: ${Spacings.SPACING_11B};
	box-sizing: border-box;
	font-family: ${({ theme }) => theme.fontFamily.inter || "'Inter', sans-serif"};

	&::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none;
	scrollbar-width: none;

	@media ${Devices.tablet} {
		height: calc(10.5 * ${Spacings.SPACING_1B});
	}
`

const MainNavLink = styled.button`
	background: ${({ active, theme }) => (active ? theme.colors.surface.card : 'transparent')};
	border: 1px solid ${({ active, theme }) => (active ? theme.colors.brand.primary : 'transparent')};
	border-radius: ${({ active, isFirst, isLast, theme }) => {
		if (!active) return '0'
		// a lone tab is both first and last — round all four corners so the group's clip doesn't cut its border
		if (isFirst && isLast) return theme.radius.lg
		if (isFirst) return `${theme.radius.lg} 0 0 ${theme.radius.lg}`
		if (isLast) return `0 ${theme.radius.lg} ${theme.radius.lg} 0`
		return '0'
	}};
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ active, theme }) => (active ? theme.colors.brand.primary : theme.colors.text.secondary)};
	padding: 0 ${Spacings.SPACING_4B};
	cursor: pointer;
	transition: all 0.2s ease-in-out;
	white-space: nowrap;
	height: 100%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	font-family: inherit;
	position: relative;
	z-index: ${({ active }) => (active ? '2' : '1')};

	@media ${Devices.tablet} {
		font-size: ${Text.MEDIUM};
		padding: 0 ${Spacings.SPACING_5B};
	}

	&:hover {
		color: ${({ theme }) => theme.colors.brand.primary};
		background: ${({ active, theme }) => (active ? theme.colors.surface.card : theme.colors.brand.subtle)};
	}
`

// allowInReadOnly: render tabs as spans so a disabled <fieldset> (history version view) can't disable them
const readOnlySafeProps = { as: 'span', role: 'tab', tabIndex: 0, 'data-version-allowed': true }

const TabsV2 = memo(({ data, onTabClick, allowInReadOnly = false, ...props }) => {
	const visibleTabs = data?.filter((tab) => !tab.hide) || []

	return (
		<LeftTabGroup {...props}>
			{visibleTabs.map((tab, idx) => {
				const isFirst = idx === 0
				const isLast = idx === visibleTabs.length - 1
				return (
					<MainNavLink
						key={tab.label || idx}
						{...(allowInReadOnly ? readOnlySafeProps : {})}
						active={tab.selected}
						isFirst={isFirst}
						isLast={isLast}
						onClick={() => {
							if (typeof onTabClick === 'function') {
								onTabClick(tab)
							} else if (typeof tab.onClick === 'function') {
								tab.onClick()
							}
						}}
					>
						{tab.label}
					</MainNavLink>
				)
			})}
		</LeftTabGroup>
	)
})

export default TabsV2
